/**
 * 전역 검색 모달 (Phase 3.1 — 도메인/엔티티 통합 검색 + W5 Commands)
 * UI_STANDARD_APPLICATION_PLAN.md §5 Phase 3.1
 * 백엔드 Knowledge Autocomplete API 연동.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Modal, Typography, List, Spin, App } from 'antd';
import type { InputRef } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { ResolvedCommand } from '@shared/business-workbench/command-resolver';
import {
  COMMAND_PALETTE_GROUP_LABELS,
  COMMAND_PALETTE_GROUP_ORDER,
  type PaletteGroupedCommands,
} from '@shared/business-workbench/workbench-command-palette-contract';
import { useTenant } from '../../contexts/TenantContext';
import { useGlobalSearch } from '../../contexts/GlobalSearchContext';
import { createBackendApiClient, type GlobalSearchResult } from '../../services/backendApiClient';
import { getListPathForBusinessDomain } from '../../utils/domainPathUtils';
import { useWorkbenchCommandContext } from '../../hooks/workbench/useWorkbenchCommandContext';
import { executeWorkbenchCommand } from '../../utils/globalSearchCommands';
import { resolvePaletteKernelDeps } from '../../workbench/workbench-palette-command-bridge';
import { PaletteIntentProposalPanel } from './PaletteIntentProposalPanel';
import { css } from '@shared/utils/omitUndefined';
import styles from './GlobalSearchModal.module.css';

const { Text } = Typography;

export type GlobalSearchModalProps = Record<string, never>;

function CommandSection({
  title,
  commands,
  onSelect,
}: {
  title: string;
  commands: ResolvedCommand[];
  onSelect: (command: ResolvedCommand) => void;
}) {
  if (commands.length === 0) {
    return null;
  }

  return (
    <section className={css(styles.commandSection)} aria-label={title}>
      <Text type="secondary" className={css(styles.sectionTitle)}>
        {title}
      </Text>
      <List
        size="small"
        dataSource={commands}
        renderItem={(command) => (
          <List.Item
            key={command.id}
            className={css(styles.listItem)}
            onClick={() => onSelect(command)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(command);
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={command.label}
          >
            <List.Item.Meta title={command.label} />
          </List.Item>
        )}
      />
    </section>
  );
}

function PaletteCommandGroups({
  grouped,
  onSelect,
}: {
  grouped: PaletteGroupedCommands;
  onSelect: (command: ResolvedCommand) => void;
}) {
  return (
    <>
      {COMMAND_PALETTE_GROUP_ORDER.map((group) => (
        <CommandSection
          key={group}
          title={COMMAND_PALETTE_GROUP_LABELS[group]}
          commands={[...grouped[group]]}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { open, query, closeSearch, setQuery } = useGlobalSearch();
  const inputRef = useRef<InputRef>(null);
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? 'default_tenant';

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GlobalSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trimmedQuery = query.trim();
  const { grouped, intentProposal, businessDomain, commandObject, commandContext } =
    useWorkbenchCommandContext({ query: trimmedQuery, tenantId });
  const hasCommands = COMMAND_PALETTE_GROUP_ORDER.some((group) => grouped[group].length > 0);
  const paletteKernelDeps = useMemo(
    () => resolvePaletteKernelDeps(tenantId, navigate),
    [navigate, tenantId]
  );

  const showEmptyState = useMemo(() => {
    if (loading) {
      return false;
    }
    if (error) {
      return false;
    }
    const knowledgeCount = result?.items?.length ?? 0;
    return !hasCommands && knowledgeCount === 0;
  }, [loading, error, hasCommands, result?.items?.length]);

  useEffect(() => {
    if (!open) {
      return;
    }
    inputRef.current?.focus({ cursor: 'end' });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setError(null);
      return;
    }
    if (!trimmedQuery) {
      setResult({ items: [], total: 0, query: '' });
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const client = createBackendApiClient(tenantId);
    client
      .searchGlobal(trimmedQuery, 20)
      .then((data) => {
        if (!cancelled) {
          setResult(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err?.message ?? '검색 중 오류가 발생했습니다.';
          setError(msg);
          message.error(msg);
          setResult({ items: [], total: 0, query });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, trimmedQuery, tenantId, message, query]);

  const handleKnowledgeSelect = (item: GlobalSearchResult['items'][0]) => {
    const path = getListPathForBusinessDomain(item.businessDomain);
    closeSearch();
    navigate(path);
  };

  const handleCommandSelect = (command: ResolvedCommand) => {
    const kernelDeps = resolvePaletteKernelDeps(tenantId, navigate);
    executeWorkbenchCommand(command, navigate, {
      ...(kernelDeps ? { kernelDeps } : {}),
    });
    closeSearch();
  };

  return (
    <Modal
      title={<span id="global-search-title">전역 검색</span>}
      open={open}
      onCancel={closeSearch}
      footer={null}
      width={560}
      destroyOnHidden
      data-testid="global-search-modal"
      aria-labelledby="global-search-title"
      aria-describedby="global-search-hint"
    >
      <Input
        ref={inputRef}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="명령 · 항목 검색 (Cmd+K)"
        aria-label="전역 검색"
        data-testid="global-search-modal-input"
        allowClear
        className={css(styles.modalSearchInput)}
      />
      <Text type="secondary" id="global-search-hint" className={css(styles.modalSearchHint)}>
        {trimmedQuery
          ? '명령을 선택하거나 아래 검색 결과를 여세요.'
          : '명령을 선택하거나 검색어를 입력하세요.'}
      </Text>
      {trimmedQuery ? (
        <>
          {loading && (
            <div className={css(styles.loadingWrap)} role="status" aria-live="polite">
              <Spin aria-label="검색 중" />
            </div>
          )}
          {!loading && error ? (
            <p role="alert" className={css(styles.errorMessage)}>
              {error}
            </p>
          ) : null}
        </>
      ) : null}
      {intentProposal ? (
        <PaletteIntentProposalPanel
          tenantId={tenantId}
          proposal={intentProposal}
          query={trimmedQuery}
          commandContext={commandContext}
          navigate={navigate}
          {...(businessDomain !== undefined ? { businessDomain } : {})}
          {...(commandObject?.id !== undefined ? { objectId: commandObject.id } : {})}
          executeOptions={{
            ...(paletteKernelDeps ? { kernelDeps: paletteKernelDeps } : {}),
          }}
        />
      ) : null}
      {hasCommands ? (
        <div className={css(styles.commandSection)}>
          <PaletteCommandGroups grouped={grouped} onSelect={handleCommandSelect} />
        </div>
      ) : null}
      {trimmedQuery && !loading && !error && result && result.items.length > 0 ? (
        <section className={css(styles.commandSection)} aria-label="검색 결과">
          <Text type="secondary" className={css(styles.sectionTitle)}>
            검색 결과
          </Text>
          <List
            size="small"
            dataSource={result.items}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                className={css(styles.listItem)}
                onClick={() => handleKnowledgeSelect(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleKnowledgeSelect(item);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`${item.displayName || item.name} - ${item.businessDomain} 목록으로 이동`}
              >
                <List.Item.Meta
                  title={item.displayName || item.name}
                  description={item.businessDomain}
                />
              </List.Item>
            )}
          />
        </section>
      ) : null}
      {trimmedQuery && showEmptyState ? (
        <p className={css(styles.emptyResult)} role="status">
          검색 결과가 없습니다.
        </p>
      ) : null}
    </Modal>
  );
};

export default GlobalSearchModal;
